/**
 * 10 ниш малого бизнеса РК.
 * Нишу определяет тип сделки (сразу платит за товар или сначала приём/смета),
 * а не отрасль по названию.
 */

import { DEAL_MODEL } from './dealModels';

export const NICHE_LIST = [
  {
    id: 'retail_offline',
    order: 1,
    label: 'Розничный магазин офлайн',
    examples: 'одежда, обувь, продукты, аптека, зоотовары, канцтовары, цветы',
    defaultDealModel: DEAL_MODEL.REPEAT,
    /** Обязательная нишевая метрика поверх базового минимума. */
    keyMetric: {
      id: 'checkDepth',
      label: 'Сколько товаров в среднем в одном чеке',
      unit: 'шт',
      hint: 'Без этого не видно, что вам выгоднее: приводить больше людей или продавать больше одному',
      defaultValue: 2,
      min: 1,
      max: 20,
    },
    optionalMetric: {
      id: 'trafficPerMonth',
      label: 'Сколько человек заходит в магазин за месяц',
      unit: 'чел',
      hint: 'Если считаете посетителей — укажите, расчёт станет точнее',
    },
  },
  {
    id: 'ecommerce',
    order: 2,
    label: 'Интернет-магазин',
    examples: 'любой товар с доставкой: сайт, маркетплейс, соцсети',
    defaultDealModel: DEAL_MODEL.REPEAT,
    keyMetric: {
      id: 'siteConversion',
      label: 'Какой процент посетителей сайта делает заказ',
      unit: '%',
      hint: 'Обычно 1–3 из 100 посетителей. Не знаете — оставьте как есть',
      defaultValue: 2,
      min: 0.1,
      max: 100,
    },
    optionalMetric: {
      id: 'abandonedCarts',
      label: 'Какой процент корзин бросают, не оплатив',
      unit: '%',
      hint: 'В среднем в мире бросают 67–70% корзин',
    },
  },
  {
    id: 'horeca',
    order: 3,
    label: 'Кафе, ресторан, доставка еды',
    examples: 'кафе, ресторан, кофейня, доставка еды',
    defaultDealModel: DEAL_MODEL.REPEAT,
    keyMetric: {
      id: 'aggregatorShare',
      label: 'Какая часть выручки приходит через агрегаторы доставки',
      unit: '%',
      hint: 'Wolt, Яндекс Еда, Choco. Они берут комиссию — это влияет на расчёт',
      defaultValue: 20,
      min: 0,
      max: 100,
    },
    optionalMetric: {
      id: 'tableTurnover',
      label: 'Сколько раз за день в среднем занимают один стол',
      unit: 'раз',
      hint: 'Только для заведений с посадкой',
    },
  },
  {
    id: 'beauty',
    order: 4,
    label: 'Салон красоты, барбершоп, мастер',
    examples: 'салон, барбершоп, мастер маникюра или бровей, спа',
    defaultDealModel: DEAL_MODEL.REPEAT,
    keyMetric: {
      id: 'occupancy',
      label: 'Насколько заполнено расписание мастеров',
      unit: '%',
      hint: 'Часто дело не в нехватке клиентов, а в свободных окнах',
      defaultValue: 65,
      min: 1,
      max: 100,
    },
    optionalMetric: {
      id: 'visitFrequency',
      label: 'Как часто клиент приходит за год',
      unit: 'раз',
      hint: 'Например, маникюр — 12–18 раз в год',
    },
  },
  {
    id: 'medical',
    order: 5,
    label: 'Медицина, стоматология, косметология',
    examples: 'стоматология, клиника, косметология с приёмом врача, ветклиника',
    defaultDealModel: DEAL_MODEL.REPEAT,
    keyMetric: {
      id: 'treatmentPlanConversion',
      label: 'Какая часть пациентов после приёма соглашается на лечение',
      unit: '%',
      hint: 'Главный рычаг в медицине — не привлечь, а довести до плана лечения',
      defaultValue: 40,
      min: 1,
      max: 100,
    },
    optionalMetric: {
      id: 'repeatPatientShare',
      label: 'Какая часть пациентов возвращается повторно',
      unit: '%',
      hint: 'Показывает, держится ли клиника на своей базе',
    },
  },
  {
    id: 'education',
    order: 6,
    label: 'Курсы, репетитор, онлайн-школа',
    examples: 'курсы, репетитор, детский кружок, онлайн-школа',
    defaultDealModel: DEAL_MODEL.ONE_TIME,
    keyMetric: {
      id: 'completionRate',
      label: 'Какая часть учеников доходит до конца курса',
      unit: '%',
      hint: 'Чем больше доходят, тем чаще покупают следующий курс',
      defaultValue: 40,
      min: 1,
      max: 100,
    },
    optionalMetric: {
      id: 'repeatPurchaseShare',
      label: 'Какая часть учеников покупает второй курс',
      unit: '%',
      hint: 'Если не считаете — пропустите',
    },
  },
  {
    id: 'b2b',
    order: 7,
    label: 'Услуги для бизнеса, консалтинг',
    examples: 'юрист, бухгалтер, маркетолог, IT-аутсорс, агентство',
    defaultDealModel: DEAL_MODEL.PROJECT,
    keyMetric: {
      id: 'dealCycleDays',
      label: 'Сколько дней в среднем проходит от первого контакта до оплаты',
      unit: 'дн',
      hint: 'Без этого нельзя честно оценить, за какое время вы вырастете',
      defaultValue: 30,
      min: 1,
      max: 365,
    },
    optionalMetric: {
      id: 'referralShare',
      label: 'Какая часть клиентов приходит по рекомендации',
      unit: '%',
      hint: 'В услугах для бизнеса это часто основной источник',
    },
  },
  {
    id: 'construction',
    order: 8,
    label: 'Строительство и ремонт',
    examples: 'бригада, дизайн интерьера, отделка',
    defaultDealModel: DEAL_MODEL.PROJECT,
    keyMetric: {
      id: 'estimateConversion',
      label: 'Какая часть замеров и смет превращается в договор',
      unit: '%',
      hint: 'Смета — самое узкое место: сюда доходят, а дальше теряются',
      defaultValue: 30,
      min: 1,
      max: 100,
    },
    optionalMetric: {
      id: 'referralShare',
      label: 'Какая часть заказов приходит по рекомендации',
      unit: '%',
      hint: 'Если не считаете — пропустите',
    },
  },
  {
    id: 'realestate',
    order: 9,
    label: 'Недвижимость',
    examples: 'агентство, риелтор',
    defaultDealModel: DEAL_MODEL.ONE_TIME,
    keyMetric: {
      id: 'showingConversion',
      label: 'Какая часть показов заканчивается сделкой',
      unit: '%',
      hint: 'Воронка длинная, поэтому средние цифры тут не работают',
      defaultValue: 15,
      min: 1,
      max: 100,
    },
    optionalMetric: {
      id: 'dealCycleDays',
      label: 'Сколько дней в среднем идёт сделка',
      unit: 'дн',
      hint: 'Обычно от 1 до 3 месяцев',
    },
  },
  {
    id: 'auto',
    order: 10,
    label: 'Автосервис, СТО',
    examples: 'СТО, шиномонтаж, детейлинг',
    defaultDealModel: DEAL_MODEL.REPEAT,
    keyMetric: {
      id: 'visitFrequency',
      label: 'Сколько раз в год к вам приезжает один клиент',
      unit: 'раз',
      hint: 'Автосервис живёт на повторных визитах, а не на новых клиентах',
      defaultValue: 2,
      min: 0.5,
      max: 24,
    },
    optionalMetric: {
      id: 'repeatClientShare',
      label: 'Какая часть клиентов приезжает повторно',
      unit: '%',
      hint: 'Если не считаете — пропустите',
    },
  },
];

export const NICHE_MAP = NICHE_LIST.reduce((acc, niche) => {
  acc[niche.id] = niche;
  return acc;
}, {});

export const getNiche = (nicheId) => NICHE_MAP[nicheId] || null;

export const isValidNiche = (nicheId) => Boolean(NICHE_MAP[nicheId]);
